/*
 * motion.js — Trio Sporting polish pass (2026-08-01).
 * Small, additive, dependency-free behaviours. Every effect respects
 * prefers-reduced-motion and no-ops gracefully if a target isn't on the page.
 *
 *  1) Header height CSS var + hide-on-scroll-down / reveal-on-scroll-up  (B1)
 *  2) Count-up on stat figures (.m-stat b)                              (W3)
 *  3) Scroll-reveal on card grids                                       (W4 / W10)
 *  4) Subtle hero parallax                                              (W8)
 *  5) Sound-button morph pulse on click                                 (W9)
 *
 *  (Photo-hero fade-in, B9, now lives as an inline synchronous <script> right after
 *  each .m-hero__media--photo element in sections/m-hero.liquid — NOT here. A `defer`red
 *  script only ever runs after first paint, so even a same-tick-true cache check added
 *  the reveal class one frame too late, producing a visible flicker on repeat visits
 *  with a warm image cache. See the comment in m-hero.liquid for the full story.)
 */
(function () {
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1) Header height var + hide/show on scroll direction ---------- */
  (function header() {
    var header = document.querySelector('.site-header');
    if (!header) return;

    function setHeaderVar() {
      document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
    }
    setHeaderVar();
    window.addEventListener('resize', setHeaderVar, { passive: true });

    var lastY = window.scrollY || 0;
    var threshold = 80;
    var ticking = false;

    function onScroll() {
      var y = window.scrollY || 0;
      var navOpen = document.querySelector('.main-nav.open');
      if (navOpen) { ticking = false; return; }

      if (y <= threshold) {
        header.classList.remove('is-hidden');
      } else if (y > lastY + 2) {
        // scrolling down
        header.classList.add('is-hidden');
      } else if (y < lastY - 2) {
        // scrolling up
        header.classList.remove('is-hidden');
      }
      lastY = y;
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(onScroll);
        ticking = true;
      }
    }, { passive: true });
  })();

  /* ---------- 2) Count-up on stat figures ---------- */
  (function countUp() {
    var stats = document.querySelectorAll('.m-stat b');
    if (!stats.length || !('IntersectionObserver' in window)) return;

    // Per-element animation state. Tracked so a fast scroll can never leave a
    // stat frozen mid-count: whenever the element loses intersection (or the
    // animation naturally completes), it lands immediately on the exact
    // original value and any in-flight rAF is cancelled.
    var state = new WeakMap();

    function finish(el) {
      var s = state.get(el);
      if (!s || s.done) return;
      if (s.raf) window.cancelAnimationFrame(s.raf);
      el.textContent = s.original;
      s.done = true;
      s.raf = null;
    }

    function animate(el) {
      var original = el.textContent.trim();
      var s = { original: original, raf: null, done: false };
      state.set(el, s);

      var match = original.match(/[\d,]+/);
      if (!match || reduceMotion) { s.done = true; return; } // non-numeric (e.g. "FEI") or reduced motion: leave as-is

      var raw = match[0];
      var target = parseInt(raw.replace(/,/g, ''), 10);
      if (!isFinite(target) || target <= 0) { s.done = true; return; }

      var prefix = original.slice(0, match.index);
      var suffix = original.slice(match.index + raw.length);
      var hasComma = raw.indexOf(',') !== -1;
      var duration = 1100;
      var start = null;

      function frame(ts) {
        if (s.done) return; // cancelled externally (e.g. scrolled out mid-flight)
        if (start === null) start = ts;
        var p = Math.min(1, (ts - start) / duration);
        var eased = 1 - Math.pow(1 - p, 3); // ease-out-cubic
        var val = Math.round(target * eased);
        var formatted = hasComma ? val.toLocaleString('en-US') : String(val);
        el.textContent = prefix + formatted + suffix;
        if (p < 1) {
          s.raf = window.requestAnimationFrame(frame);
        } else {
          finish(el); // p reached 1: land on the exact original string
        }
      }
      s.raf = window.requestAnimationFrame(frame);
    }

    // Starts the count-up once a stat is meaningfully in view.
    var startIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !state.has(entry.target)) {
          animate(entry.target);
        }
      });
    }, { threshold: 0.6 });

    // Safety net: a fast scroll can carry a still-animating stat fully out of
    // view before it finishes. Land it on the final value immediately instead
    // of leaving whatever intermediate number the last rendered frame showed.
    var guardIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) finish(entry.target);
      });
    }, { threshold: 0 });

    stats.forEach(function (el) {
      startIO.observe(el);
      guardIO.observe(el);
    });

    // Absolute backstop: never leave a wrong number showing if the page is
    // hidden/unloaded mid-animation.
    window.addEventListener('pagehide', function () { stats.forEach(finish); });
  })();

  /* ---------- 3) Scroll-reveal on card grids ---------- */
  (function reveal() {
    if (!('IntersectionObserver' in window)) return;
    var selectors = [
      '.m-tier-grid > .m-tier',
      '.m-quote-cards > .m-qcard',
      // [2026-08-04, fix-wave #32] '.m-cols-3 > .m-col' intentionally removed — the
      // translateY(20px)->0 reveal + per-column stagger on "What your membership unlocks"
      // read as the whole band "not being pinned, shifting on scroll" (CEO review). Every
      // .m-cols-3 instance now renders in its final position immediately, no motion.
      '.m-stats > .m-stat',
      '.m-ways > .m-way',
      '.m-split > *'
    ];
    var groups = {};
    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        var parent = el.parentElement;
        var key = sel + '::' + (parent ? Array.prototype.indexOf.call(document.querySelectorAll(sel.split(' > ')[0]), parent) : 0);
        if (!groups[key]) groups[key] = [];
        groups[key].push(el);
      });
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

    Object.keys(groups).forEach(function (key) {
      groups[key].forEach(function (el, i) {
        el.setAttribute('data-reveal', '');
        el.style.transitionDelay = reduceMotion ? '0ms' : Math.min(i * 70, 420) + 'ms';
        io.observe(el);
      });
    });
  })();

  /* ---------- 4) Subtle hero parallax (10-20%) ---------- */
  (function parallax() {
    if (reduceMotion) return;
    var targets = document.querySelectorAll('[data-parallax]');
    if (!targets.length) return;
    var ticking = false;
    var amplitude = 0.1; // 10% — subtle, clipped by the hero's own overflow:hidden
    var maxPx = 60; // clamp so tall sections don't reveal an edge gap

    function apply() {
      targets.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        var vh = window.innerHeight || 1;
        if (rect.bottom < 0 || rect.top > vh) return; // out of view, skip
        var progress = (rect.top) / vh; // ~1 at bottom of viewport entry, ~-1 past top
        var offset = progress * rect.height * amplitude * -1;
        offset = Math.max(-maxPx, Math.min(maxPx, offset));
        el.style.transform = 'translateY(' + offset.toFixed(1) + 'px)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(apply); ticking = true; }
    }, { passive: true });
    window.addEventListener('resize', function () {
      if (!ticking) { window.requestAnimationFrame(apply); ticking = true; }
    }, { passive: true });
    apply();
  })();

  /* ---------- 5) Sound-button morph pulse ---------- */
  (function soundMorph() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.m-hero__sound');
      if (!btn) return;
      btn.classList.remove('is-toggled');
      // force reflow so the animation can retrigger on rapid clicks
      void btn.offsetWidth;
      btn.classList.add('is-toggled');
      window.setTimeout(function () { btn.classList.remove('is-toggled'); }, 400);
    });
  })();
})();
